import hyperdom from 'hyperdom'
import assetPath from '../assetPath.js'
import genesysChatCustomForm from './genesysChatCustomForm.js'
import i18n from './i18n.json'

class GenesysChat {
  constructor (options) {
    this.dmpg = options.dmpg
    this.config = options.config
    this.user = options.user
    this.bookingSummary = options.bookingSummary
    this.coBrowseEnabled = options.coBrowseEnabled
    this.formJSON = genesysChatCustomForm(this.config.brandPages, this.user, this.dmpg)
    this.initConfig()
    this.injectScript(this.config.genesysChatUrl)
    this.chatOpened = false
    this.coBrowseOpened = false
    this.eventsAdded = false
    this.isMinimized = false
  }

  get genesysCommands () {
    return {
      WEBCHAT_OPEN: 'WebChat.open',
      COBROWSE_START: 'CoBrowse.start',
    }
  }

  get genesysEvents () {
    return {
      WEBCHAT_SERVICE_ERROR: 'WebChatService.error',
      WEBCHAT_OPENED: 'WebChat.opened',
      WEBCHAT_SUBMITTED: 'WebChat.submitted',
      WEBCHAT_STARTED: 'WebChat.started',
      WEBCHAT_MESSAGE_ADDED: 'WebChat.messageAdded',
      WEBCHAT_MINIMIZED: 'WebChat.minimized',
      WEBCHAT_UNMINIMIZED: 'WebChat.unminimized',
      WEBCHAT_CLOSED: 'WebChat.closed',
      WEBCHAT_REJECTED: 'WebChat.rejected',
      COBROWSE_STARTED: 'CoBrowse.started',
      COBROWSE_STOPPED: 'CoBrowse.stopped',
    }
  }

  get chatInteractManualEvents () {
    return {
      MANUAL_ERROR: 'chat.interact.manual.error',
      MANUAL_CLICK_OPEN: 'chat.interact.manual.click.open',
      MANUAL_CLICK_START: 'chat.interact.manual.click.start',
      MANUAL_CLICK_SEND: 'chat.interact.manual.message.send',
      MANUAL_CLICK_MINIMIZE: 'chat.interact.manual.click.minimize',
      MANUAL_CLICK_EXPAND: 'chat.interact.manual.click.expand',
      MANUAL_CLICK_CLOSE: 'chat.interact.manual.click.close',
    }
  }

  getGenesysWrapperPosition () {
    const bookingSummary = document.querySelector(".booking-summary")
    const bookingSummaryBottom = document.querySelector(".booking-summary-bottom")

    let bookingSummaryHeight = 0
    let bookingSummaryBottomHeight = 0

    if (bookingSummary) {
      bookingSummaryHeight = parseInt(getComputedStyle(bookingSummary).getPropertyValue('height'), 10)
      bookingSummaryBottomHeight = parseInt(getComputedStyle(bookingSummaryBottom).getPropertyValue('height'), 10)
    }

    return this.bookingSummary.showExpandedSummary ? bookingSummaryBottomHeight + bookingSummaryHeight : bookingSummaryBottomHeight
  }

  render () {
    if (!this.scriptLoaded) {
      return
    }

    const bottom = this.getGenesysWrapperPosition()
    if (this.chatOpened) {
      const chatMinimizedStyle = document.querySelector('.cx-webchat').style
      chatMinimizedStyle.transform = `translate(0px, ${this.isMinimized ? -bottom : 0}px)`
    } else {
      return <div class='genesys-wrapper' style={`bottom: ${bottom}px`}>
        { this.renderButton(this.config.features.genesysChat.chatButtonName, 'chat-icon', this.genesysCommands.WEBCHAT_OPEN, {formJSON: this.formJSON}) }
      </div>
    }
  }

  renderButton (title, image, command, options) {
    return <div class='genesys-wrapper-button'>
      <button onclick={() => {
        this.addEvents()
        window.CXBus.command(command, options)
      }}>
        <img src={assetPath(`/public/img/genesysChat/${image}.svg`)}/>
        <span>{title}</span>
      </button>
    </div>
  }

  initConfig () {
    if (!window._genesys) {
      window._genesys = {}
    }
    if (!window._gt) {
      window._gt = []
    }

    const {
      genesysChatServiceUrl,
      genesysCoBrowserSrc,
      genesysCoBrowserUrl,
      sellingCompanyCode,
      styleCode: brandCode,
    } = this.config
    const {location} = this.config.features.genesysChat

    const widgetConfig = {
      main: {
        debug: false,
        theme: 'light',
        lang: 'en',
        preload: [
          'sidebar',
        ],
        i18n,
        mobileMode: 'auto',
        mobileModeBreakpoint: 1000,
      },
      webchat: {
        form: this.formJSON,
        dataURL: genesysChatServiceUrl,
        userData: {
          chatsource: 'BookingEngine',
          cus_type: this.user.isTravelAgent ? 'Travel Agency' : 'Customer',
          selling_co: sellingCompanyCode,
          cc_loc: location,
          selling_brand: brandCode,
          chatscreening: this.user.isTravelAgent ? `${sellingCompanyCode} Travel Agency BookingEngine` : `${sellingCompanyCode} Customer BookingEngine`,
        },
        formValidation: true,
        emojis: true,
      },
      cobrowse: {
        src: genesysCoBrowserSrc,
        url: genesysCoBrowserUrl,
      },
    }

    window._genesys.widgets = widgetConfig
  }

  injectScript (genesysChatUrl) {
    const genesysChatScript = document.createElement('script')
    genesysChatScript.src = `${genesysChatUrl}/cxbus.min.js`
    genesysChatScript.addEventListener('load', () => {
      // eslint-disable-next-line no-undef
      CXBus.configure({debug: false, pluginsPath: `${genesysChatUrl}/plugins/`})
      // eslint-disable-next-line no-undef
      CXBus.loadPlugin('widgets-core')
      this.scriptLoaded = true
      this.refresh()
    })
    document.head.appendChild(genesysChatScript)
  }

  addEvents (){
    if(!this.eventsAdded){
      const busEvent = window._genesys.widgets.bus

      busEvent.subscribe(this.genesysEvents.WEBCHAT_SERVICE_ERROR, () =>{
        this.sendDMPGEvent(this.chatInteractManualEvents.MANUAL_ERROR)
      })
      busEvent.subscribe(this.genesysEvents.WEBCHAT_REJECTED, () =>{
        this.sendDMPGEvent(this.chatInteractManualEvents.MANUAL_ERROR)
      })
      busEvent.subscribe(this.genesysEvents.WEBCHAT_OPENED, () =>{
        this.sendDMPGEvent(this.chatInteractManualEvents.MANUAL_CLICK_OPEN)
        this.chatOpened = true

        if (this.coBrowseEnabled) {
          const footerButton = document.createElement('div')
          footerButton.innerHTML = `<a class="genesys-cobrowse-button">${this.config.features.genesysChat.coBrowseButtonName}</a>`
          footerButton.onclick = () => window.CXBus.command(this.genesysCommands.COBROWSE_START)
          const footer = document.querySelector('.cx-footer')

          const poweredBy = footer.querySelector('.cx-powered-by')
          poweredBy.removeChild(poweredBy.firstChild)

          footer.appendChild(footerButton)
        }

        this.refresh()
      })
      busEvent.subscribe(this.genesysEvents.WEBCHAT_SUBMITTED, (data) => {
        this.dmpg.sendImmediately({
          event: 'form.load.manual.dataLayer.formSubmit',
          form: {
            ...data,
            name: 'genesys',
          },
        })

        const footer = document.querySelector('.cx-footer')
        if (footer) {
          footer.style.setProperty('display', 'none', 'important')
        }
      })
      busEvent.subscribe(this.genesysEvents.WEBCHAT_STARTED, () => {
        this.sendDMPGEvent(this.chatInteractManualEvents.MANUAL_CLICK_START)
      })
      busEvent.subscribe(this.genesysEvents.WEBCHAT_MESSAGE_ADDED, (data) => {
        this.sendDMPGEvent(this.chatInteractManualEvents.MANUAL_CLICK_SEND, data)
      })
      busEvent.subscribe(this.genesysEvents.WEBCHAT_MINIMIZED, () =>{
        this.isMinimized = true
        this.refresh()
        this.sendDMPGEvent(this.chatInteractManualEvents.MANUAL_CLICK_MINIMIZE)
      })
      busEvent.subscribe(this.genesysEvents.WEBCHAT_UNMINIMIZED, () =>{
        this.isMinimized = false
        this.refresh()
        this.sendDMPGEvent(this.chatInteractManualEvents.MANUAL_CLICK_EXPAND)
      })
      busEvent.subscribe(this.genesysEvents.WEBCHAT_CLOSED, () =>{
        this.sendDMPGEvent(this.chatInteractManualEvents.MANUAL_CLICK_CLOSE)
        this.chatOpened = false
        this.refresh()
      })
      busEvent.subscribe(this.genesysEvents.COBROWSE_STARTED, () =>{
        this.coBrowseOpened = true
        this.refresh()
      })
      busEvent.subscribe(this.genesysEvents.COBROWSE_STOPPED, () =>{
        this.coBrowseOpened = false
        this.refresh()
      })
      this.eventsAdded = true
    }
  }

  sendDMPGEvent (event, data) {
    this.dmpg.sendImmediately({
      event: event,
      chat: {
        ...data,
        name: 'genesys',
      },
    })
  }

}

export default GenesysChat
